<template>
  <div class="date-request-event-details--container">
    <div>
      <div class="date-request-event-details">
        <!-- Date line -->
        <div class="event-detail-line">
          <div v-if="formattedDate" class="flex gap-1 items-center">
            <IconCalendar class="event-detail-icon" />
            <span class="event-detail-text">{{ formattedDate }}</span>
          </div>
          <IconPeople class="event-detail-icon" />
          <span class="event-detail-text">{{ peopleText }}</span>
          <IconLocation class="event-detail-icon" />
          <span class="event-detail-text">{{ location }}</span>
        </div>
      </div>

      <!-- Price line -->
      <div class="event-detail-line">
        <div
          v-if="loadingPricing"
          class="w-20 h-5 bg-gray-200 animate-pulse rounded-md"
        />
        <span v-else class="event-detail-price">{{ formattedPrice }}</span>
      </div>
    </div>

    <!-- Preview event button -->
    <Button
      v-if="!hideButton"
      class="preview-event-button"
      @click.prevent="handlePreviewEvent"
    >
      <span>{{ t('product.dateRequest.eventSuggestions.previewButton') }}</span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import IconCalendar from '~/assets/icons/calendar.svg?component'
import IconPeople from '~/assets/icons/event/people.svg?component'
import IconLocation from '~/assets/icons/event/event-info-location.svg?component'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import type {
  DateRequestSimilarEvent,
  Pricings,
} from '~/composables/types/event'
import { getPricingTotalPrice } from '~/utils/pricingGetters'

const { t, locale } = useI18n()

const { savedFormData } = useDateRequestFormStorage()

interface Props {
  item: DateRequestSimilarEvent
  hideButton?: boolean
  private?: boolean
  pricingData?: Pricings
  loadingPricing?: boolean
}

const {
  item,
  hideButton = false,
  private: _privateEvent = false,
  pricingData = undefined,
  loadingPricing = false,
} = defineProps<Props>()

const emit = defineEmits<{
  previewEvent: [id: DateRequestSimilarEvent]
}>()

const formattedDate = computed(() => {
  // Extend dayjs with UTC and timezone plugins
  dayjs.extend(utc)
  dayjs.extend(timezone)

  const bookableDate = item.bookable_date?.data
  if (!bookableDate?.start) return undefined

  const date = dayjs(bookableDate.start, { utc: true })
  return date.format('DD.MM.YYYY (HH:mm)')
})

// Format people count
const peopleText = computed(() => {
  return t('product.dateRequest.eventSuggestions.people', {
    numberOfTickets: savedFormData.value?.numberOfTickets,
  })
})

// Get location from the event data
const location = computed(() => {
  // Fallback to address if available
  return item?.address?.data?.locality?.data?.name
})

// Format price - use pricing data from API if available, otherwise fallback to simple calculation
const formattedPrice = computed(() => {
  // If we have pricing data from the API, use it (most accurate)
  if (pricingData) {
    const totalPrice = getPricingTotalPrice(pricingData)
    return totalPrice?.formatted
  }

  // Fallback to simple calculation from event data
  const bookableDate = item.bookable_date?.data
  const priceAmount =
    bookableDate?.product?.price?.amount ?? Number(item.default_price) * 100

  if (priceAmount != null) {
    const numberOfTickets = savedFormData.value?.numberOfTickets ?? 1
    const totalAmount = Number(priceAmount) * numberOfTickets

    return formatPriceAmount(totalAmount, locale.value)
  }

  return undefined
})

// Handle preview event click
const handlePreviewEvent = () => {
  emit('previewEvent', item)
}
</script>

<style lang="postcss" scoped>
.date-request-event-details--container {
  @apply flex flex-col justify-between h-full;
}

.date-request-event-details {
  @apply flex flex-col lg:flex-row gap-2;
}

.event-detail-line {
  @apply flex items-center gap-1 text-xs mt-1;
}

.event-detail-icon {
  @apply w-4 h-4 text-gray-600;
}

.event-detail-text {
  @apply text-gray-600;
}

.event-detail-price {
  @apply font-semibold text-gray-800 text-sm mt-1;
}

.preview-event-button {
  @apply mt-3 md:w-fit border border-teal-900 bg-white text-teal-900 font-semibold py-1 px-4 rounded-lg text-sm hover:bg-teal-900 hover:text-white transition-colors;
}
</style>
